import { ProgressStatus, formatChapterId } from '../db';
import { getAudioUrl } from './audio';

let cachedPlan = null;

export async function loadPlan() {
  if (cachedPlan) return cachedPlan;
  const response = await fetch('/plan.json');
  if (!response.ok) throw new Error('Failed to load reading plan');
  cachedPlan = await response.json();
  return cachedPlan;
}

/** Day 1 = 2 January of the current local year (matches biblein365 scrape). */
export function planDayNumber(now = new Date()) {
  const start = new Date(now.getFullYear(), 0, 2);
  start.setHours(0, 0, 0, 0);
  const today = new Date(now);
  today.setHours(0, 0, 0, 0);
  const day = Math.floor((today - start) / 86400000) + 1;
  if (day < 1) return 1;
  if (day > 364) return 364;
  return day;
}

export function isChapterComplete(chapterProgress, bookId, chapter) {
  const id = formatChapterId(bookId, chapter);
  return chapterProgress[id]?.status === ProgressStatus.COMPLETED;
}

export function isChapterStarted(chapterProgress, bookId, chapter) {
  const id = formatChapterId(bookId, chapter);
  const status = chapterProgress[id]?.status;
  return status && status !== ProgressStatus.NOT_STARTED;
}

/** First hole in the section — completed chapters later in the plan do not fill an earlier gap. */
export function firstUnread(section, chapterProgress) {
  if (!section?.chapters?.length) return null;
  for (const ch of section.chapters) {
    if (!isChapterComplete(chapterProgress, ch.bookId, ch.chapter)) return ch;
  }
  return null;
}

/** Positive = days behind (first unread was scheduled that many days ago). */
export function daysBehind(unread, todayDay = planDayNumber()) {
  if (!unread) return 0;
  return todayDay - unread.day;
}

export function chapterIndex(section, bookId, chapter) {
  if (!section?.chapters) return -1;
  return section.chapters.findIndex(
    (c) => c.bookId === bookId && c.chapter === chapter
  );
}

export function nextInPlan(section, bookId, chapter) {
  const i = chapterIndex(section, bookId, chapter);
  if (i < 0) return null;
  return section.chapters[i + 1] || null;
}

/** Next chapter after this one that is not already completed. */
export function nextUnreadInPlan(section, bookId, chapter, chapterProgress) {
  if (!section?.chapters?.length) return null;
  const i = chapterIndex(section, bookId, chapter);
  const start = i < 0 ? 0 : i + 1;
  for (let j = start; j < section.chapters.length; j++) {
    const ch = section.chapters[j];
    if (!isChapterComplete(chapterProgress, ch.bookId, ch.chapter)) return ch;
  }
  return null;
}

export function prevInPlan(section, bookId, chapter) {
  const i = chapterIndex(section, bookId, chapter);
  if (i <= 0) return null;
  return section.chapters[i - 1] || null;
}

/** Which plan track contains this chapter. Optional preferId wins if it still matches. */
export function sectionForChapter(plan, bookId, chapter, preferId) {
  const sections = Object.values(plan?.sections || {});
  if (preferId && plan?.sections?.[preferId] && chapterIndex(plan.sections[preferId], bookId, chapter) >= 0) {
    return plan.sections[preferId];
  }
  return sections.find((s) => chapterIndex(s, bookId, chapter) >= 0) || null;
}

export function toPlayable(ch, planSection) {
  if (!ch) return null;
  return {
    bookId: ch.bookId,
    book: ch.book,
    chapter: ch.chapter,
    url: getAudioUrl(ch.bookId, ch.book, ch.chapter),
    fromPlan: true,
    ...(planSection ? { planSection } : {}),
  };
}
